/**
 * Keyring client (main thread).
 *
 * Provides:
 *
 *   1. KeyringClient — promise-based RPC (Remote Procedure Call) over
 *      MessagePort to the SharedWorker. Handles request correlation,
 *      timeouts, and lock-event broadcasts.
 *
 *   2. RemoteSignerIdentity — adapter that implements the SignerIdentity
 *      interface by forwarding to the keyring. Application code uses this
 *      everywhere a SignerIdentity is expected; the rest of the system
 *      doesn't know it's talking through a worker.
 *
 * The keyring also produces storage-key bytes on demand; those are imported
 * as non-extractable CryptoKey objects on the main thread, so even if the
 * main thread is compromised the raw key bytes only live briefly.
 */

import { ed25519 } from '@noble/curves/ed25519';

import type {
  Bytes,
  Context,
  LinkageProof,
  PublicKey,
  Signature,
  SignerIdentity,
} from '../identity';
import type {
  Envelope,
  KeyringRequest,
  KeyringResponse,
  StatusResponse,
} from './keyring.protocol';

// ─────────────────────────────────────────────────────────────────────────────
// CLIENT
// ─────────────────────────────────────────────────────────────────────────────

interface PendingCall {
  resolve: (r: KeyringResponse) => void;
  reject: (e: Error) => void;
  timeoutHandle: ReturnType<typeof setTimeout>;
}

export class KeyringClient {
  #port: MessagePort;
  #pending = new Map<string, PendingCall>();
  #seq = 0;
  #lockListeners = new Set<() => void>();

  constructor(port: MessagePort) {
    this.#port = port;
    port.onmessage = (e: MessageEvent<Envelope<KeyringResponse>>) => this.#onMessage(e.data);
    port.start();
  }

  /**
   * Connect to the keyring SharedWorker.
   *
   * Accepts either:
   *   - A `URL`/string pointing at the worker script (legacy path), OR
   *   - A `SharedWorker` constructor (Vite's `?sharedworker` import gives
   *     one of these — preferred because it embeds the correctly-bundled
   *     worker URL at build time).
   *
   * Tests pass a `MessagePort` directly via the public constructor.
   */
  static async connect(
    source: string | URL | { new(options?: WorkerOptions): SharedWorker },
  ): Promise<KeyringClient> {
    if (typeof SharedWorker === 'undefined') {
      throw new Error('SharedWorker not available in this environment');
    }
    let sw: SharedWorker;
    if (typeof source === 'function') {
      // Vite's `?sharedworker` default export is a class.
      sw = new source({ type: 'module', name: 'surface-mesh-keyring' });
    } else {
      sw = new SharedWorker(source, { type: 'module', name: 'surface-mesh-keyring' });
    }
    return new KeyringClient(sw.port);
  }

  #onMessage(env: Envelope<KeyringResponse>): void {
    // Broadcast (no correlation id we own) — distribute to lock listeners.
    if (env.id.startsWith('broadcast:lock')) {
      this.#lockListeners.forEach((l) => l());
      return;
    }
    const pending = this.#pending.get(env.id);
    if (!pending) return;
    this.#pending.delete(env.id);
    clearTimeout(pending.timeoutHandle);
    if (env.msg.kind === 'error') {
      pending.reject(new Error(env.msg.message));
    } else {
      pending.resolve(env.msg);
    }
  }

  async #call<T extends KeyringResponse>(msg: KeyringRequest, timeoutMs = 10_000): Promise<T> {
    const id = `c${++this.#seq}`;
    return new Promise<T>((resolve, reject) => {
      const timeoutHandle = setTimeout(() => {
        this.#pending.delete(id);
        reject(new Error(`Keyring call timed out: ${msg.kind}`));
      }, timeoutMs);
      this.#pending.set(id, {
        resolve: (r) => resolve(r as T),
        reject,
        timeoutHandle,
      });
      this.#port.postMessage({ id, msg } satisfies Envelope<KeyringRequest>);
    });
  }

  onLock(fn: () => void): () => void {
    this.#lockListeners.add(fn);
    return () => this.#lockListeners.delete(fn);
  }

  // ─── public API ──────────────────────────────────────

  async unlock(seed: string, password: string, autoLockMs?: number) {
    const req: KeyringRequest = autoLockMs !== undefined
      ? { kind: 'unlock', seed, password, autoLockMs }
      : { kind: 'unlock', seed, password };
    return this.#call(req, 120_000);
  }

  async generate(password: string, autoLockMs?: number) {
    const req: KeyringRequest = autoLockMs !== undefined
      ? { kind: 'generate', password, autoLockMs }
      : { kind: 'generate', password };
    return this.#call(req, 120_000);
  }

  async lock() {
    return this.#call({ kind: 'lock' });
  }

  async status(): Promise<StatusResponse> {
    const r = await this.#call({ kind: 'status' });
    if (r.kind !== 'status') throw new Error('Unexpected response');
    return r;
  }

  async touch() {
    return this.#call({ kind: 'touch' });
  }

  async sign(payload: Bytes, context: Context): Promise<Signature> {
    const r = await this.#call({ kind: 'sign', payload, context });
    if (r.kind !== 'sign') throw new Error('Unexpected response');
    return r.signature;
  }

  async verify(payload: Bytes, signature: Signature, context: Context, pubkey: PublicKey): Promise<boolean> {
    const r = await this.#call({ kind: 'verify', payload, signature, context, pubkey });
    if (r.kind !== 'verify') throw new Error('Unexpected response');
    return r.ok;
  }

  async pubkey(context: Context): Promise<PublicKey> {
    const r = await this.#call({ kind: 'pubkey', context });
    if (r.kind !== 'pubkey') throw new Error('Unexpected response');
    return r.pubkey;
  }

  async proveLinkage(ctxA: Context, ctxB: Context): Promise<LinkageProof> {
    const r = await this.#call({ kind: 'linkage', ctxA, ctxB });
    if (r.kind !== 'linkage') throw new Error('Unexpected response');
    return { ctxA: r.ctxAHash, ctxB: r.ctxBHash, proof: r.proof, zeroKnowledge: r.zeroKnowledge };
  }

  async storageKey(domain: 'envelope' | 'session' | 'blob'): Promise<CryptoKey> {
    const r = await this.#call({ kind: 'storageKey', domain });
    if (r.kind !== 'storageKey') throw new Error('Unexpected response');
    // Import as non-extractable so even compromised main-thread code can't
    // read the raw bytes after this point.
    // .slice() copies into a fresh ArrayBuffer-backed Uint8Array, which
    // satisfies importKey's `ArrayBufferView<ArrayBuffer>` signature in
    // TS 5.7+ (SharedArrayBuffer is no longer assignable).
    const key = await crypto.subtle.importKey(
      'raw',
      r.keyBytes.slice(),
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt'],
    );
    // Wipe the local copy.
    r.keyBytes.fill(0);
    return key;
  }

  async exportBackup(password: string): Promise<Uint8Array> {
    const r = await this.#call({ kind: 'exportBackup', password }, 30_000);
    if (r.kind !== 'exportBackup') throw new Error('Unexpected response');
    return r.blob;
  }

  async importBackup(blob: Uint8Array, password: string, autoLockMs?: number) {
    const req: KeyringRequest = autoLockMs !== undefined
      ? { kind: 'importBackup', blob, password, autoLockMs }
      : { kind: 'importBackup', blob, password };
    return this.#call(req, 30_000);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// REMOTE SIGNER IDENTITY ADAPTER
// ─────────────────────────────────────────────────────────────────────────────
// Application code receives one of these and uses it the same way it would
// use a local SignerIdentity. The difference is invisible at the call site.

export class RemoteSignerIdentity implements SignerIdentity {
  #client: KeyringClient;
  #fingerprint: string;

  constructor(client: KeyringClient, fingerprint: string) {
    this.#client = client;
    this.#fingerprint = fingerprint;
  }

  sign(payload: Bytes, context: Context): Promise<Signature> {
    return this.#client.sign(payload, context);
  }

  async verify(payload: Bytes, signature: Signature, context: Context, pubkey: PublicKey): Promise<boolean> {
    // Verification can be local — pubkey is public, no secret involved.
    // This saves a worker round-trip on every envelope check.
    return ed25519.verify(signature, payload, pubkey);
  }

  pubkey(context: Context): Promise<PublicKey> {
    return this.#client.pubkey(context);
  }

  proveLinkage(ctxA: Context, ctxB: Context): Promise<LinkageProof> {
    return this.#client.proveLinkage(ctxA, ctxB);
  }

  async lock(): Promise<void> {
    await this.#client.lock();
  }

  fingerprint(): string {
    return this.#fingerprint;
  }
}
